import { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpenCheck,
  Bot,
  Braces,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  FileJson,
  FileOutput,
  FolderOpen,
  LoaderCircle,
  RotateCcw,
  Route,
  Save,
  Settings2,
  ShieldAlert,
  Sparkles,
} from "lucide-react";

import { appPath, isPublicDeployment, paidUpgradeUrl } from "./deployment";
import { DocxPreviewDialog } from "./components/DocxPreviewDialog";
import { AiControlDialog } from "./components/AiControlDialog";
import { EditorPanel } from "./components/EditorPanel";
import { OutlinePanel, type ActivePanel } from "./components/OutlinePanel";
import { PreviewPanel } from "./components/PreviewPanel";
import { TemplateDrawer } from "./components/TemplateDrawer";
import {
  createAffiliation,
  createInitialManuscript,
  createManuscriptFigure,
  createManuscriptTable,
  createReference,
  createSectionForParent,
  insertChildSection,
  insertSectionAfterTree,
  appendAuthor,
  moveAuthorById,
  moveItemById,
  moveSectionById,
  removeAffiliationById,
  removeAuthorById,
  removeSectionTreeById,
  setCoCorrespondingAuthor,
  setCoFirstAuthor,
  updateAuthorById,
  validateManuscript,
  type Manuscript,
  type ManuscriptAffiliation,
  type ManuscriptAuthor,
  type ManuscriptReference,
  type ManuscriptSection,
  type ManuscriptTable,
  type ManuscriptFigure,
  type StructuredAbstractKey,
} from "./domain/manuscript";
import {
  AI_FORMAT_PATHS,
  applyAiControlPlan,
  type AiControlResult,
} from "./domain/ai-control";
import {
  getJournalSuggestions,
  getJournalProfileCapabilities,
  matchJournalProfile,
} from "./domain/journal-profiles";
import {
  builtInTemplates,
  conservativeSciTemplate,
  normalizeTemplate,
  type ManuscriptTemplate,
} from "./domain/templates";
import {
  createWorkspaceFingerprint,
  isWorkspaceExportCurrent,
  parseSavedWorkspace,
  type SavedWorkspace,
} from "./domain/workspace";
import {
  EXPORT_CONTRACT_VERSION,
  parseExportHealthResponse,
  parseExportResponse,
  readApiError,
  type ExportHealth,
  type ExportResponse,
} from "./domain/export-contract";

const STORAGE_KEY = "sci-manuscript-studio.workspace.v1";

declare global {
  interface Window {
    sciStudioAI?: {
      version: 1;
      getState: () => unknown;
      getCapabilities: () => unknown;
      apply: (plan: unknown) => Promise<Pick<AiControlResult, "applied" | "messages">>;
      exportWord: () => Promise<ExportResponse>;
    };
  }
}

function loadWorkspace(): SavedWorkspace {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) throw new Error("empty");
    return parseSavedWorkspace(JSON.parse(raw));
  } catch {
    return { manuscript: createInitialManuscript(), customTemplates: [] };
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function safeFilename(value: string) {
  return value.replace(/[\\/:*?"<>|]+/g, "_").trim() || "SCI稿件";
}

export default function App() {
  const initial = useMemo(loadWorkspace, []);
  const [manuscript, setManuscript] = useState(initial.manuscript);
  const [customTemplates, setCustomTemplates] = useState(
    initial.customTemplates,
  );
  const [activeId, setActiveId] = useState<ActivePanel>("metadata");
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [zoom, setZoom] = useState(48);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResponse | null>(null);
  const [exportError, setExportError] = useState("");
  const [exportHealth, setExportHealth] = useState<ExportHealth | null>(null);
  const [docxPreviewOpen, setDocxPreviewOpen] = useState(false);
  const [aiControlOpen, setAiControlOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const exportLockRef = useRef(false);

  const templates = useMemo(
    () => [...builtInTemplates, ...customTemplates],
    [customTemplates],
  );
  const currentTemplate =
    templates.find((item) => item.id === manuscript.templateId) ??
    conservativeSciTemplate;
  const validation = useMemo(
    () => validateManuscript(manuscript, currentTemplate),
    [currentTemplate, manuscript],
  );
  const journalMatch = useMemo(
    () =>
      matchJournalProfile(
        manuscript.submissionRoute === "journal"
          ? manuscript.metadata.journal
          : "",
      ),
    [manuscript.metadata.journal, manuscript.submissionRoute],
  );
  const journalSuggestions = useMemo(getJournalSuggestions, []);
  const workspaceFingerprint = useMemo(
    () => createWorkspaceFingerprint(manuscript, currentTemplate),
    [currentTemplate, manuscript],
  );
  const workspaceFingerprintRef = useRef(workspaceFingerprint);
  workspaceFingerprintRef.current = workspaceFingerprint;

  useEffect(() => {
    let cancelled = false;
    void fetch(appPath("/api/health"), { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Word 导出服务未就绪");
        return parseExportHealthResponse(await response.json());
      })
      .then((health) => {
        if (!cancelled) setExportHealth(health);
      })
      .catch(() => {
        if (!cancelled) setExportHealth(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            manuscript,
            customTemplates,
          } satisfies SavedWorkspace),
        );
        setSavedAt(new Date());
        setSaveError("");
      } catch {
        setSaveError("自动保存失败，请立即保存项目 JSON");
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [customTemplates, manuscript]);

  useEffect(() => {
    setExportResult(null);
    setDocxPreviewOpen(false);
  }, [workspaceFingerprint]);

  const touch = (next: Manuscript): Manuscript => ({
    ...next,
    updatedAt: new Date().toISOString(),
  });

  const updateMetadata = (
    field: keyof Manuscript["metadata"],
    value: string,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        name:
          field === "title" && value.trim()
            ? value.trim().slice(0, 60)
            : current.name,
        metadata: { ...current.metadata, [field]: value },
        submissionRoute:
          field === "journal" && value.trim()
            ? "journal"
            : current.submissionRoute,
      }),
    );

  const selectSubmissionRoute = (route: Manuscript["submissionRoute"]) =>
    setManuscript((current) =>
      touch({
        ...current,
        submissionRoute: route,
        metadata:
          route === "generic"
            ? { ...current.metadata, journal: "" }
            : current.metadata,
      }),
    );

  const updateAbstract = (
    field: "mode" | "content" | "keywords",
    value: string,
  ) =>
    setManuscript((current) => {
      if (field === "keywords") return touch({ ...current, keywords: value });
      return touch({
        ...current,
        abstract: { ...current.abstract, [field]: value },
      } as Manuscript);
    });

  const updateStructuredAbstract = (
    field: StructuredAbstractKey,
    value: string,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        abstract: {
          ...current.abstract,
          structured: { ...current.abstract.structured, [field]: value },
        },
      }),
    );

  const updateSection = (
    id: string,
    patch: Partial<Pick<ManuscriptSection, "title" | "content">>,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        sections: current.sections.map((section) =>
          section.id === id ? { ...section, ...patch } : section,
        ),
      }),
    );

  const addAuthor = () =>
    setManuscript((current) =>
      touch({
        ...current,
        authors: appendAuthor(current.authors),
      }),
    );

  const updateAuthor = (
    id: string,
    patch: Partial<Omit<ManuscriptAuthor, "id">>,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        authors: updateAuthorById(current.authors, id, patch),
      }),
    );

  const moveAuthor = (id: string, direction: -1 | 1) =>
    setManuscript((current) =>
      touch({
        ...current,
        authors: moveAuthorById(current.authors, id, direction),
      }),
    );

  const updateCoFirstAuthor = (id: string, checked: boolean) =>
    setManuscript((current) =>
      touch({
        ...current,
        authors: setCoFirstAuthor(current.authors, id, checked),
      }),
    );

  const updateCoCorrespondingAuthor = (id: string, checked: boolean) =>
    setManuscript((current) =>
      touch({
        ...current,
        authors: setCoCorrespondingAuthor(current.authors, id, checked),
      }),
    );

  const deleteAuthor = (id: string) => {
    const author = manuscript.authors.find((item) => item.id === id);
    if (!author) return;
    if (
      (author.name.trim() || author.email.trim() || author.orcid.trim()) &&
      !window.confirm(`删除作者“${author.name || "未命名作者"}”及其标记？`)
    ) {
      return;
    }
    setManuscript((current) =>
      touch({
        ...current,
        authors: removeAuthorById(current.authors, id),
      }),
    );
  };

  const addAffiliation = () =>
    setManuscript((current) =>
      touch({
        ...current,
        affiliations: [...current.affiliations, createAffiliation()],
      }),
    );

  const updateAffiliation = (
    id: string,
    patch: Partial<Omit<ManuscriptAffiliation, "id">>,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        affiliations: current.affiliations.map((affiliation) =>
          affiliation.id === id ? { ...affiliation, ...patch } : affiliation,
        ),
      }),
    );

  const deleteAffiliation = (id: string) => {
    const affiliation = manuscript.affiliations.find((item) => item.id === id);
    if (!affiliation) return;
    const linkedAuthors = manuscript.authors.filter((author) =>
      author.affiliationIds.includes(id),
    ).length;
    if (
      (affiliation.name.trim() || linkedAuthors) &&
      !window.confirm(
        `删除机构“${affiliation.name || "未命名机构"}”？${linkedAuthors ? ` 将同时解除 ${linkedAuthors} 名作者的关联。` : ""}`,
      )
    ) {
      return;
    }
    setManuscript((current) => {
      const next = removeAffiliationById(
        current.affiliations,
        current.authors,
        id,
      );
      return touch({ ...current, ...next });
    });
  };

  const addReference = () =>
    setManuscript((current) =>
      touch({
        ...current,
        references: [...current.references, createReference()],
      }),
    );

  const bulkAddReferences = (items: string[]) =>
    setManuscript((current) =>
      touch({
        ...current,
        references: [
          ...current.references,
          ...items.map((text) => ({ ...createReference(), text })),
        ],
      }),
    );

  const updateReference = (
    id: string,
    patch: Partial<Omit<ManuscriptReference, "id">>,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        references: current.references.map((reference) =>
          reference.id === id ? { ...reference, ...patch } : reference,
        ),
      }),
    );

  const moveReference = (id: string, direction: -1 | 1) =>
    setManuscript((current) =>
      touch({
        ...current,
        references: moveItemById(current.references, id, direction),
      }),
    );

  const deleteReference = (id: string) => {
    const reference = manuscript.references.find((item) => item.id === id);
    if (!reference) return;
    if (
      (reference.text.trim() ||
        reference.doi.trim() ||
        reference.pmid.trim() ||
        reference.url.trim()) &&
      !window.confirm("删除这条参考文献？")
    ) {
      return;
    }
    setManuscript((current) =>
      touch({
        ...current,
        references: current.references.filter((item) => item.id !== id),
      }),
    );
  };

  const addTable = (rows: string[][]) =>
    setManuscript((current) =>
      touch({
        ...current,
        tables: [...current.tables, createManuscriptTable(rows)],
      }),
    );

  const updateTable = (
    id: string,
    patch: Partial<Omit<ManuscriptTable, "id">>,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        tables: current.tables.map((table) =>
          table.id === id ? { ...table, ...patch } : table,
        ),
      }),
    );

  const moveTable = (id: string, direction: -1 | 1) =>
    setManuscript((current) =>
      touch({
        ...current,
        tables: moveItemById(current.tables, id, direction),
      }),
    );

  const deleteTable = (id: string) => {
    const table = manuscript.tables.find((item) => item.id === id);
    if (!table) return;
    if (!window.confirm("删除这张三线表及其全部单元格？")) return;
    setManuscript((current) =>
      touch({
        ...current,
        tables: current.tables.filter((item) => item.id !== id),
      }),
    );
  };

  const addFigure = () =>
    setManuscript((current) =>
      touch({
        ...current,
        figures: [...current.figures, createManuscriptFigure()],
      }),
    );

  const updateFigure = (
    id: string,
    patch: Partial<Omit<ManuscriptFigure, "id">>,
  ) =>
    setManuscript((current) =>
      touch({
        ...current,
        figures: current.figures.map((figure) =>
          figure.id === id ? { ...figure, ...patch } : figure,
        ),
      }),
    );

  const moveFigure = (id: string, direction: -1 | 1) =>
    setManuscript((current) =>
      touch({
        ...current,
        figures: moveItemById(current.figures, id, direction),
      }),
    );

  const deleteFigure = (id: string) => {
    const figure = manuscript.figures.find((item) => item.id === id);
    if (!figure) return;
    if (
      (figure.title.trim() || figure.legend.trim()) &&
      !window.confirm("删除这条图注？")
    ) {
      return;
    }
    setManuscript((current) =>
      touch({
        ...current,
        figures: current.figures.filter((item) => item.id !== id),
      }),
    );
  };

  const addSection = (parentId?: string) => {
    const section = createSectionForParent(
      manuscript.sections,
      parentId ?? null,
    );
    if (!section) return;
    setManuscript((current) =>
      touch({
        ...current,
        sections: section.parentId
          ? insertChildSection(current.sections, section)
          : insertSectionAfterTree(current.sections, section),
      }),
    );
    setActiveId(section.id);
  };

  const moveSection = (id: string, direction: -1 | 1) =>
    setManuscript((current) =>
      touch({
        ...current,
        sections: moveSectionById(current.sections, id, direction),
      }),
    );

  const deleteSection = (id: string) => {
    const section = manuscript.sections.find((item) => item.id === id);
    if (!section) return;
    const descendants = manuscript.sections.filter((item) => item.parentId === id);
    if (
      (section.content.trim() || descendants.length) &&
      !window.confirm(`删除“${section.title}”及其全部下级标题和正文？`)
    ) {
      return;
    }
    const remainingSections = removeSectionTreeById(manuscript.sections, id);
    setManuscript((current) =>
      touch({
        ...current,
        sections: removeSectionTreeById(current.sections, id),
      }),
    );
    if (
      activeId === id ||
      (manuscript.sections.some((item) => item.id === activeId) &&
        !remainingSections.some((item) => item.id === activeId))
    ) {
      setActiveId("abstract");
    }
  };

  const changeTemplate = (id: string) =>
    setManuscript((current) => touch({ ...current, templateId: id }));

  const duplicateTemplate = () => {
    const id = `custom-${crypto.randomUUID()}`;
    const copy = normalizeTemplate({
      ...currentTemplate,
      id,
      name: `${currentTemplate.name} · 自定义`,
      description: "本机自定义模板；在核对学校或期刊官方要求前保持草稿状态。",
      authority: "school",
      status: "draft",
      sourceUrl: "",
      verifiedOn: "",
    });
    setCustomTemplates((current) => [...current, copy]);
    changeTemplate(id);
  };

  const updateTemplate = (candidate: ManuscriptTemplate) => {
    if (!customTemplates.some((item) => item.id === candidate.id)) {
      const id = `custom-${crypto.randomUUID()}`;
      const copy = normalizeTemplate({
        ...candidate,
        id,
        name: `${candidate.name} · 自定义`,
        authority: "school",
        status: "draft",
        verifiedOn: "",
      });
      setCustomTemplates((current) => [...current, copy]);
      changeTemplate(id);
      return;
    }
    const normalized = normalizeTemplate(candidate);
    setCustomTemplates((current) =>
      current.map((item) => (item.id === normalized.id ? normalized : item)),
    );
  };

  const deleteTemplate = () => {
    const isCustom = customTemplates.some(
      (item) => item.id === currentTemplate.id,
    );
    if (!isCustom) return;
    if (!window.confirm(`删除自定义模板“${currentTemplate.name}”？`)) return;
    setCustomTemplates((current) =>
      current.filter((item) => item.id !== currentTemplate.id),
    );
    setManuscript((current) =>
      touch({ ...current, templateId: conservativeSciTemplate.id }),
    );
    setTemplateDrawerOpen(false);
  };

  const exportProjectJson = () => {
    const payload = JSON.stringify(
      { manuscript, customTemplates } satisfies SavedWorkspace,
      null,
      2,
    );
    downloadBlob(
      new Blob([payload], { type: "application/json" }),
      `${safeFilename(manuscript.name)}.sci-project.json`,
    );
  };

  const importProjectJson = async (file: File) => {
    try {
      const parsed = parseSavedWorkspace(JSON.parse(await file.text()));
      setManuscript(parsed.manuscript);
      setCustomTemplates(parsed.customTemplates);
      setActiveId("metadata");
      setExportResult(null);
      setExportError("");
    } catch {
      setExportError("项目格式不受支持、内容损坏或包含重复标识符。");
    }
  };

  const resetProject = () => {
    if (!window.confirm("新建空白稿件？当前稿件仍可先导出为项目 JSON。")) return;
    setManuscript(createInitialManuscript());
    setActiveId("metadata");
    setExportResult(null);
    setExportError("");
  };

  const exportDocx = async (): Promise<ExportResponse> => {
    if (!validation.canExport) {
      throw new Error("当前稿件存在阻断项，不能请求导出 Word");
    }
    if (exportLockRef.current) {
      const error = new Error("已有 Word 导出任务正在运行");
      setExportError(error.message);
      throw error;
    }
    const hasCurrentExportService =
      exportHealth?.ok &&
      exportHealth.exportContractVersion === EXPORT_CONTRACT_VERSION &&
      exportHealth.capabilities?.includes("content-fidelity-gate-v1");
    if (!hasCurrentExportService) {
      const error = new Error(
        "Word 导出服务版本过旧或未就绪，请重新启动 SCI 稿格后再导出。",
      );
      setExportError(error.message);
      throw error;
    }
    exportLockRef.current = true;
    const requestFingerprint = workspaceFingerprint;
    const requestBody = JSON.stringify({
      manuscript,
      template: currentTemplate,
    });
    setExporting(true);
    setExportError("");
    setExportResult(null);
    setDocxPreviewOpen(false);
    try {
      const response = await fetch(appPath("/api/exports"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestBody,
      });
      const payload: unknown = await response.json();
      if (!response.ok) {
        throw new Error(readApiError(payload, "DOCX 导出失败"));
      }
      const data = parseExportResponse(payload);
      const routedData: ExportResponse = {
        ...data,
        downloadUrl: appPath(data.downloadUrl),
        reportUrls: Object.fromEntries(
          Object.entries(data.reportUrls).map(([name, url]) => [name, appPath(url)]),
        ) as ExportResponse["reportUrls"],
      };
      if (
        !isWorkspaceExportCurrent(
          requestFingerprint,
          workspaceFingerprintRef.current,
        )
      ) {
        throw new Error("稿件在导出期间已修改；旧结果已作废，请重新导出。");
      }
      setExportResult(routedData);
      return routedData;
    } catch (reason) {
      const error = reason instanceof Error ? reason : new Error("DOCX 导出失败");
      setExportError(error.message);
      throw error;
    } finally {
      exportLockRef.current = false;
      setExporting(false);
    }
  };

  const applyAiPlan = async (
    plan: unknown,
  ): Promise<Pick<AiControlResult, "applied" | "messages">> => {
    const result = applyAiControlPlan(manuscript, currentTemplate, plan);
    setManuscript(result.manuscript);
    if (JSON.stringify(result.template) !== JSON.stringify(currentTemplate)) {
      updateTemplate(result.template);
    }
    return { applied: result.applied, messages: result.messages };
  };

  useEffect(() => {
    const api: NonNullable<Window["sciStudioAI"]> = {
      version: 1,
      getState: () =>
        structuredClone({
          manuscript,
          template: currentTemplate,
          validation,
          journalMatch,
          export: {
            health: exportHealth,
            result: exportResult,
            error: exportError,
            running: exporting,
          },
        }),
      getCapabilities: () => ({
        actionProtocol: "sci-studio-ai/v1",
        submissionRoutes: ["generic", "journal"],
        actions: [
          "set_submission_route",
          "set_metadata",
          "set_keywords",
          "set_abstract_mode",
          "set_structured_abstract",
          "set_unstructured_abstract",
          "add_section",
          "update_section",
          "add_reference",
          "add_figure",
          "update_figure",
          "set_format",
          "set_emphasis_rules",
        ],
        formatPaths: [...AI_FORMAT_PATHS],
        safeguards: [
          "schema-validation",
          "no-author-fact-invention",
          "export-audit-required",
          "journal-profile-is-reference-until-officially-verified",
          "literal-emphasis-rules-do-not-rewrite-text",
        ],
        journalProfiles: getJournalProfileCapabilities(),
      }),
      apply: applyAiPlan,
      exportWord: async () => {
        if (!validation.canExport) {
          throw new Error("当前稿件存在阻断项，不能请求导出 Word");
        }
        return exportDocx();
      },
    };
    window.sciStudioAI = api;
    return () => {
      if (window.sciStudioAI === api) delete window.sciStudioAI;
    };
  });

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-workspace">
        跳到稿件编辑区
      </a>
      <header className="topbar">
        <div className="brand-block">
          <div className="brand-mark">
            <BookOpenCheck size={22} />
          </div>
          <div>
            <strong>SCI 稿格</strong>
            <span>{isPublicDeployment ? "免费自助排版工作台" : "结构化排版工作台"}</span>
          </div>
          <span className="local-badge">{isPublicDeployment ? "FREE" : "LOCAL"}</span>
        </div>

        <div className="project-title-block">
          <span>当前项目</span>
          <strong>{manuscript.name}</strong>
        </div>

        <div className="topbar-actions">
          <div className={`autosave-status ${saveError ? "error" : ""}`}>
            <Save size={13} />
            <span>
              {saveError ||
                (savedAt ? `${savedAt.toLocaleTimeString()} 已保存` : "正在保存")}
            </span>
          </div>
          {!isPublicDeployment ? (
            <button
              className="ai-control-button"
              onClick={() => setAiControlOpen(true)}
              type="button"
            >
              <Bot size={15} /> AI 高级接口
            </button>
          ) : null}
          <a
            className="paid-service-link"
            href={paidUpgradeUrl}
            rel="noreferrer"
            target="_blank"
          >
            <ExternalLink size={15} /> 人工精排
          </a>
          <button className="icon-text-button" onClick={resetProject} type="button">
            <RotateCcw size={15} /> 新建
          </button>
          <button
            className="icon-text-button"
            onClick={() => fileInputRef.current?.click()}
            type="button"
          >
            <FolderOpen size={15} /> 载入
          </button>
          <button className="icon-text-button" onClick={exportProjectJson} type="button">
            <FileJson size={15} /> 保存项目
          </button>
          <input
            accept="application/json,.json"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void importProjectJson(file);
              event.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
        </div>
      </header>

      <section className="controlbar">
        <div className="submission-route-wrap">
          <div className="control-icon route-icon">
            <Route size={16} />
          </div>
          <div className="route-mode-block">
            <span className="control-label">投稿路线</span>
            <div aria-label="投稿路线" className="route-segments" role="group">
              <button
                aria-pressed={manuscript.submissionRoute === "generic"}
                className={
                  manuscript.submissionRoute === "generic" ? "active" : ""
                }
                onClick={() => selectSubmissionRoute("generic")}
                type="button"
              >
                通用 SCI
              </button>
              <button
                aria-pressed={manuscript.submissionRoute === "journal"}
                className={
                  manuscript.submissionRoute === "journal" ? "active" : ""
                }
                onClick={() => selectSubmissionRoute("journal")}
                type="button"
              >
                指定期刊
              </button>
            </div>
          </div>

          {manuscript.submissionRoute === "journal" ? (
            <>
              <label className="top-route-field journal-field">
                <span>目标期刊</span>
                <input
                  list="journal-name-suggestions"
                  onChange={(event) =>
                    updateMetadata("journal", event.target.value)
                  }
                  placeholder="输入或选择期刊"
                  value={manuscript.metadata.journal}
                />
                <small className={journalMatch.matched ? "matched" : ""}>
                  {manuscript.metadata.journal.trim()
                    ? journalMatch.matched
                      ? `已匹配 ${journalMatch.displayName} · 官网待核验`
                      : "未命中内置配置，不会猜测"
                    : "输入后自动匹配配置与清单"}
                </small>
                <datalist id="journal-name-suggestions">
                  {journalSuggestions.map((journal) => (
                    <option key={journal} value={journal} />
                  ))}
                </datalist>
              </label>
              <label className="top-route-field article-type-field">
                <span>文章类型</span>
                <input
                  onChange={(event) =>
                    updateMetadata("articleType", event.target.value)
                  }
                  placeholder="Original Article / Review"
                  value={manuscript.metadata.articleType}
                />
                <small>用于核对该期刊的具体要求</small>
              </label>
            </>
          ) : (
            <div className="generic-route-summary">
              <span>当前路线</span>
              <strong>SCI 保守通用稿</strong>
              <small>未指定期刊时使用，不冒充期刊模板</small>
            </div>
          )}

        </div>

        <div className="template-select-wrap compact">
          <div className="control-icon">
            <Sparkles size={16} />
          </div>
          <label>
            <span>版式模板</span>
            <div className="select-shell">
              <select
                onChange={(event) => changeTemplate(event.target.value)}
                value={currentTemplate.id}
              >
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} />
            </div>
          </label>
          <span className={`authority-chip ${currentTemplate.status}`}>
            {currentTemplate.status === "verified"
              ? "已核验"
              : currentTemplate.status === "baseline"
                ? "通用基线"
                : "自定义草稿"}
          </span>
          <button
            className="template-settings-button"
            onClick={() => setTemplateDrawerOpen(true)}
            type="button"
          >
            <Settings2 size={15} /> 设计模板
          </button>
        </div>

        <button
          className="export-button"
          disabled={
            !validation.canExport ||
            exporting ||
            !exportHealth?.ok ||
            exportHealth.exportContractVersion !== EXPORT_CONTRACT_VERSION
          }
          onClick={() => void exportDocx().catch(() => undefined)}
          type="button"
        >
          {exporting ? <LoaderCircle className="spin" size={17} /> : <FileOutput size={17} />}
          {exporting
            ? "正在生成与审计"
            : exportHealth?.ok && exportHealth.exportContractVersion === EXPORT_CONTRACT_VERSION
              ? "导出 Word"
              : "导出服务未就绪"}
        </button>
      </section>

      {isPublicDeployment ? (
        <section className="public-service-note" aria-label="免费工具说明">
          <span>
            免费自助版：内容只在导出时上传，生成文件保留 24 小时。通用基线不等于目标期刊官方模板。
          </span>
          <a href={paidUpgradeUrl} rel="noreferrer" target="_blank">
            需要精确期刊适配或人工复核？走付费服务
          </a>
        </section>
      ) : null}

      {(exportResult || exportError) && (
        <section className={`export-banner ${exportError ? "error" : exportResult?.status}`}>
          <div>
            {exportError || exportResult?.status !== "passed" ? (
              <ShieldAlert size={18} />
            ) : (
              <CheckCircle2 size={18} />
            )}
            <span>
              <strong>
                {exportError
                  ? "导出未完成"
                  : `${exportResult?.delivery.label}｜${exportResult?.audit.summary}`}
              </strong>
              <small>
                {exportError ||
                  `${exportResult?.audit.contentFidelity} · ${exportResult?.audit.deterministic} · ${exportResult?.audit.postflight} · ${exportResult?.audit.realRender}`}
              </small>
              {exportResult?.contentWarnings.length ? (
                <small className="export-issues">
                  待补：{exportResult.contentWarnings.join("；")}
                </small>
              ) : null}
              {exportResult?.auditWarnings.length ? (
                <small className="export-issues">
                  审计提示：{exportResult.auditWarnings.join("；")}
                </small>
              ) : null}
              {exportResult ? (
                <small className="export-disclaimer">{exportResult.delivery.disclaimer}</small>
              ) : null}
            </span>
          </div>
          {exportResult ? (
            <div>
              <button onClick={() => setDocxPreviewOpen(true)} type="button">
                <Braces size={14} /> 快速预览
              </button>
              {exportResult.reportUrls.repair ? (
                <a
                  href={exportResult.reportUrls.repair}
                  rel="noreferrer"
                  target="_blank"
                >
                  <ShieldAlert size={14} /> 审计报告
                </a>
              ) : null}
              {exportResult.reportUrls.contentFidelity ? (
                <a
                  href={exportResult.reportUrls.contentFidelity}
                  rel="noreferrer"
                  target="_blank"
                >
                  <BookOpenCheck size={14} /> 内容保真
                </a>
              ) : null}
              <a href={exportResult.downloadUrl}>
                <Download size={14} />
                {exportResult.delivery.state === "submission_ready"
                  ? "下载投稿稿"
                  : exportResult.delivery.state === "review_required"
                    ? "下载待核验稿"
                    : "下载内部草稿"}
              </a>
              <a
                className="upgrade-service"
                href={paidUpgradeUrl}
                rel="noreferrer"
                target="_blank"
              >
                <ExternalLink size={14} /> 人工复核 / 精确适配
              </a>
            </div>
          ) : null}
        </section>
      )}

      <div className="workspace-grid" id="main-workspace" tabIndex={-1}>
        <OutlinePanel
          activeId={activeId}
          manuscript={manuscript}
          onAdd={addSection}
          onDelete={deleteSection}
          onMove={moveSection}
          onSelect={setActiveId}
        />
        <EditorPanel
          activeId={activeId}
          journalMatch={journalMatch}
          manuscript={manuscript}
          onAddAffiliation={addAffiliation}
          onAddAuthor={addAuthor}
          onAddReference={addReference}
          onAddTable={addTable}
          onAbstractChange={updateAbstract}
          onAffiliationChange={updateAffiliation}
          onAuthorChange={updateAuthor}
          onCoCorrespondingChange={updateCoCorrespondingAuthor}
          onCoFirstChange={updateCoFirstAuthor}
          onBulkAddReferences={bulkAddReferences}
          onDeleteAffiliation={deleteAffiliation}
          onDeleteAuthor={deleteAuthor}
          onDeleteReference={deleteReference}
          onDeleteTable={deleteTable}
          onAddFigure={addFigure}
          onFigureChange={updateFigure}
          onMoveFigure={moveFigure}
          onDeleteFigure={deleteFigure}
          onMetadataChange={updateMetadata}
          onMoveAuthor={moveAuthor}
          onMoveReference={moveReference}
          onMoveTable={moveTable}
          onOpenTemplateDesigner={() => setTemplateDrawerOpen(true)}
          onReferenceChange={updateReference}
          onSectionChange={updateSection}
          onAddChildSection={addSection}
          onStructuredAbstractChange={updateStructuredAbstract}
          onTemplateChange={updateTemplate}
          onTableChange={updateTable}
          template={currentTemplate}
        />
        <PreviewPanel
          manuscript={manuscript}
          onZoomChange={setZoom}
          template={currentTemplate}
          validation={validation}
          zoom={zoom}
        />
      </div>

      <TemplateDrawer
        custom={customTemplates.some((item) => item.id === currentTemplate.id)}
        onChange={updateTemplate}
        onClose={() => setTemplateDrawerOpen(false)}
        onDelete={deleteTemplate}
        onDuplicate={duplicateTemplate}
        open={templateDrawerOpen}
        template={currentTemplate}
      />
      <DocxPreviewDialog
        downloadUrl={exportResult?.downloadUrl ?? ""}
        onClose={() => setDocxPreviewOpen(false)}
        open={docxPreviewOpen}
      />
      <AiControlDialog
        canExport={
          validation.canExport &&
          !exporting &&
          Boolean(exportHealth?.ok) &&
          exportHealth?.exportContractVersion === EXPORT_CONTRACT_VERSION
        }
        onApply={applyAiPlan}
        onClose={() => setAiControlOpen(false)}
        onExport={() => void exportDocx().catch(() => undefined)}
        open={aiControlOpen}
        snapshot={{
          manuscript,
          template: currentTemplate,
          validation,
          journalMatch,
        }}
      />
    </div>
  );
}
